import React from 'react';
import { Progress, Tag } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { DashboardOutlined, RiseOutlined } from '@ant-design/icons';
import DeliveryAlertButton from './DeliveryAlertButton';
import TripDownloadButton from '../Reports/TripDetailsReport';
import type { Route } from '../../Types/Summary.types';
import { formatDateTime, getSuccessRateColor } from '../../Utils/Summary.utils';
import { fetchTripReportData } from '../../store/trips.store';
import { AppDispatch } from '../../store/store';

const tagColumn = (title: string, dataIndex: keyof Route, key: string, color: string) => ({
  title,
  dataIndex,
  key,
  align: 'center' as const,
  render: (val: unknown) => <Tag color={color}>{val as React.ReactNode}</Tag>,
});

const dateColumn = (title: string, dataIndex: keyof Route, key: string, color: string) => ({
  title,
  dataIndex,
  key,
  align: 'center' as const,
  render: (val: string | null) => formatDateTime(val, color),
});

export const routeTablePagination: TablePaginationConfig = {
  defaultPageSize: 5,
  pageSizeOptions: ['5', '10', '25', '50'],
  showSizeChanger: true,
  showQuickJumper: true,
  position: ['bottomRight'],
  size: 'small',
  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
};

export const getRouteTableColumns = (dispatch: AppDispatch): ColumnsType<Route> => {
  const handleFetchTrip = async (tripId: number, licenseNo: string) => {
    const result = await dispatch(fetchTripReportData({ tripID: tripId, licenseNo })).unwrap();
    return result;
  };

  return [
    {
      title: 'Delivery Alert',
      key: 'deliveryAlert',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const planned = Number(record.total_deliveries);
        const actual = Number(record.actual_deliveries);

        if (planned > 0 && planned !== actual) {
          return <DeliveryAlertButton planned={planned} actual={actual} />;
        }
        return <span />;
      },
    },
    {
      title: 'Route Id',
      dataIndex: 'trip_id',
      key: 'tripId',
      defaultSortOrder: 'descend',
      sorter: (a: Route, b: Route) => a.trip_id - b.trip_id,
    },
    tagColumn('Total Planned Deliveries', 'total_deliveries', 'totalDeliveries', 'blue'),
    tagColumn('Pending Deliveries', 'planned_deliveries', 'plannedDeliveries', 'orange'),
    tagColumn('Actual Delivered Deliveries', 'actual_deliveries', 'actualDeliveries', 'green'),
    tagColumn('Cancelled Deliveries', 'cancelled_deliveries', 'cancelledDeliveries', 'red'),
    tagColumn('Skipped Deliveries', 'skipped_deliveries', 'skippedDeliveries', 'red'),
    dateColumn('Departure Time (Planned)', 'departure', 'departure', 'blue'),
    dateColumn('Departure Time (Actual)', 'delivery_start', 'departureTimeActual', 'orange'),
    dateColumn('Arrival Time (Planned)', 'arrival', 'arrival', 'blue'),
    dateColumn('Arrival Time (Actual)', 'delivery_end', 'arrivalTimeActual', 'orange'),
    {
      title: 'Planned Waiting Time (min)',
      dataIndex: 'waiting_time',
      key: 'waitingTime',
      align: 'center',
      render: (val) => <Tag color="purple">{val || '-'}</Tag>,
    },
    {
      title: 'Success Rate',
      key: 'successRate',
      align: 'center',
      render: (_, record) => {
        const actual = Number(record.actual_deliveries);
        const planned = Number(record.total_deliveries);

        if (!planned || Number.isNaN(actual) || Number.isNaN(planned)) {
          return <Tag color="gray">N/A</Tag>;
        }

        const rate = (actual / planned) * 100;
        return (
          <Progress
            percent={Number(rate.toFixed(2))}
            size="small"
            strokeColor={getSuccessRateColor(rate)}
            format={(percent) => `${percent?.toFixed(0)}%`}
          />
        );
      },
    },
    {
      title: 'Actual Distance (Km)',
      dataIndex: 'distance_actual',
      key: 'distanceActual',
      render: (value: number) => (value !== null ? (value / 1000).toFixed(2) : 'N/A'),
    },
    {
      title: 'Distance Optimized (Km)',
      dataIndex: 'distance_optimized',
      key: 'distanceOptimized',
      render: (value: number) => (value !== null ? (value / 1000).toFixed(2) : 'N/A'),
    },
    {
      title: 'Estimated Distance (Km)',
      dataIndex: 'distance_unoptimized',
      key: 'distanceUnoptimized',
      render: (value: number) => (value !== null ? (value / 1000).toFixed(2) : 'N/A'),
    },
    {
      title: 'Vehicle Id',
      dataIndex: 'vehicle_id',
      key: 'vehicle_id',
    },
    {
      title: 'Vehicle Optimum Capacity',
      dataIndex: 'vehicle_capacity',
      key: 'vehicleCapacity',
      align: 'right',
      render: (val) => (
        <Tag icon={<DashboardOutlined />} color="geekblue">
          {val}
        </Tag>
      ),
    },
    {
      title: 'Vehicle Utilized Capacity',
      dataIndex: 'locations_capacity',
      key: 'locationsCapacity',
      align: 'right',
      render: (val) => (
        <Tag icon={<RiseOutlined />} color="purple">
          {val}
        </Tag>
      ),
    },
    {
      title: 'Download Report',
      key: 'downloadReport',
      align: 'center' as const,
      width: 130,
      render: (_: any, record: Route) => (
        <div
          data-no-row-click="true"
          role="presentation"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()} // ← add keyboard handler
        >
          <TripDownloadButton tripId={record.trip_id} fetchTripDetails={handleFetchTrip} />
        </div>
      ),
    },
    {
      title: 'User Id',
      dataIndex: 'user_id',
      key: 'user_id',
    },
  ];
};
